/**
 * Barre d'état du téléphone
 *
 * Le problème : `viewport-fit=cover` fait dessiner la WebView bord à bord, donc
 * **sous** la barre d'état. Le fond de page passe derrière l'horloge,
 * mais sans compensation les titres d'écran se retrouvent dessous, illisibles.
 *
 * La compensation par `env(safe-area-inset-top)` est la bonne façon de faire, et
 * elle est en place dans `almanach.css`. Ce module en est la ceinture : certaines
 * versions de la WebView Android rapportent une encoche nulle alors qu'elles
 * dessinent bien sous la barre, et le CSS n'a alors rien à compenser. On demande
 * donc explicitement à Android de **réserver** la place.
 *
 * Les deux se composent sans se marcher dessus : quand Android réserve la place,
 * l'encoche vaut zéro et la règle CSS n'ajoute rien ; quand il ne la réserve pas,
 * l'encoche est renseignée et le CSS compense. Aucun des deux cas ne double le
 * décalage.
 *
 * `style: LIGHT` est contre-intuitif et c'est pour cela que la configuration le
 * commente : dans le vocabulaire du greffon, il désigne le style *du contenu*
 * clair, donc des icônes **sombres** — ce qu'il faut sur le fond crème de l’application.
 */

import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

/** Fond de l'application en clair. Doit suivre `--fond` dans almanach.css. */
const FOND_CLAIR = '#f6f5f1';
/** Et en sombre, pour le thème sombre du système. */
const FOND_SOMBRE = '#1c1c1e';

export async function regleBarreEtat(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const sombre = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

    // Android réserve la place de la barre : le contenu ne passe plus dessous.
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setBackgroundColor({ color: sombre ? FOND_SOMBRE : FOND_CLAIR });
    await StatusBar.setStyle({ style: sombre ? Style.Dark : Style.Light });
  } catch {
    // Greffon absent, plateforme sans barre d'état réglable : sans conséquence,
    // la règle CSS reste en place.
  }
}
